<template>
  <div class="hashtags">
    <modal v-if="isVisible" @close="close">
      <template #header>
        <h2 class="hashtags__heading">Hashtags</h2>
      </template>
      <template #body>
        <ul class="hashtags__list">
          <li v-for="tag in tags" :key="tag.name" class="hashtags__list-item">
            <hash-tag
              :no-cursor-on-hover="true"
              :label="tag.name"
              :theme="theme"
              @click="$emit('search', tag)"
            ></hash-tag>
          </li>
        </ul>
      </template>
    </modal>
    <ul class="hashtags__list">
      <li
        v-for="tag in computedTagList"
        :key="tag.name"
        class="hashtags__list-item"
      >
        <hash-tag
          :label="tag.name"
          :theme="theme"
          :disabled="disableTags"
          @click="searchHashtag(tag)"
        ></hash-tag>
      </li>

      <li
        v-if="shouldRenderEllipsis || forceShowMore"
        class="hashtags__list-item"
      >
        <button @click="open" class="hashtags__button">
          <hash-tag :theme="theme"></hash-tag>
        </button>
      </li>
    </ul>
  </div>
</template>

<script>
import HashTag from './HashTag';
import Modal from '../Utils/Modal';

export default {
  name: 'hash-tag-list',
  // Vue3 (LP-4/MR-78 emits parity): declare `search` (emitted from a method AND
  // the template `@click="$emit('search', tag)"`) so it binds as a component event.
  emits: ['search'],
  components: {
    HashTag,
    Modal,
  },
  props: {
    /**
     * Set true to show full list of hashtags without the modal
     *
     * @type {Boolean}
     */
    forceShowMore: {
      type: Boolean,
      default: false,
    },
    tags: Array,
    theme: {
      type: Object,
      validator(value) {
        return ['light', 'dark', 'custom'].includes(value.type);
      },
    },
    characterLimit: Number,
    disableTags: Boolean,
  },
  data() {
    return {
      isVisible: false,
      hashtagLimit: 6,
    };
  },
  computed: {
    computedTagList() {
      /**
       * Reduces hashtag names into one string
       *
       * @return {string}
       */
      function getJoinedText(tags) {
        const joined = tags.reduce((a, b) => {
          return { name: `${a.name} ${b.name}` };
        });

        return joined.name;
      }

      /**
       * Splices an array
       *
       * @return {array}
       */
      function getSpliced(arr, limit) {
        return [...arr].slice(0, limit);
      }

      if (this.shouldRenderEllipsis && !this.forceShowMore) {
        // Use a LOCAL counter seeded from hashtagLimit each recompute — the fit
        // loop no longer mutates this.hashtagLimit (PS-5, no-side-effects-in-
        // computed-properties). This also fixes the never-reset bug: the old code
        // only ever decremented the instance property, so once a narrow viewport
        // shrank the list a later wider recompute stayed stuck; a fresh local
        // counter lets the list grow back to the full limit.
        let limit = this.hashtagLimit;
        let tags = getSpliced(this.tags, limit);
        let tagsText = getJoinedText(tags);

        // Iterate and remove hashtags if character count exceeds limit
        while (tagsText.length > this.characterLimit && limit > 0) {
          limit--;
          tags = getSpliced(this.tags, limit);
          tagsText = getJoinedText(tags);
        }

        return tags;
      }

      return this.tags;
    },

    shouldRenderEllipsis() {
      return this.tags.length > 6;
    },
  },
  methods: {
    open() {
      this.isVisible = true;
    },

    close() {
      this.isVisible = false;
    },

    searchHashtag(tag) {
      if (this.disableTags) {
        return;
      }

      this.$emit('search', tag);
    },
  },
};
</script>

<style lang="scss" scoped>
@import '../../assets/variables-v2';

.hashtags {
  margin-bottom: 8px;
  min-height: 64px;
  &__list {
    display: flex;
    justify-content: flex-start;
    align-items: center;
    flex-wrap: wrap;
    margin: 0 -0.25rem;
  }
  &__list-item {
    padding: 0 0.25rem;
    list-style: none;
    min-width: 0;
  }
  &__heading {
    font-family: $font;
    font-size: 1.25rem;
    margin-bottom: 1rem;
  }
  &__button {
    background: transparent;
    border: 0;
    font-family: $font;
  }
}
</style>
